import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    Injector,
    linkedSignal,
    signal,
    untracked,
    viewChild,
    WritableSignal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { WorkflowService } from "../service/workflow.service";
import { Task } from "../../../pojo/Task";
import { S25Util } from "../../../util/s25-util";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { UserprefService } from "../../../services/userpref.service";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { Workflow } from "../service/Workflow";
import WorkflowItem = Workflow.WorkflowItem;
import WorkflowStatus = Workflow.WorkflowStatus;
import { DataIngestionModule } from "../../data-ingestion/data.ingestion.module";
import { LinkedFormsComponent } from "../../data-ingestion/linked-forms.component";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { INGESTION_SYSTEM_FIELDS } from "../../data-ingestion/data.ingestion.const";
import { isPrimitiveFieldType } from "../../data-ingestion/data.ingestion.const";
import { s25Resource } from "../../../util/s25.resource";
import { MyWorkflowChatDropdownComponent } from "./my.workflow.chat.dropdown.component";
import { Proto } from "../../../pojo/Proto";
import ISODateString = Proto.ISODateString;
import { SeSecurityStore } from "../../student-engagement/se.security.store";
import { AlertService } from "../../../services/alert.service";
import { StateStore } from "../../../services/state/state.store";
import { S25States } from "../../../services/state/state.map";
import { StateService } from "../../../services/state.service";
import { S25DatepickerModule } from "../../s25-datepicker/s25.datepicker.module";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { NgTemplateOutlet } from "@angular/common";
import { Contact } from "../../../pojo/Contact";
import { WorkflowDiagramViewerComponent } from "../diagram/workflow.diagram.viewer.component";
import { DiagramChatRequest } from "../diagram/workflow.diagram.model";
import { WorkflowChatController } from "../chat/workflow.chat.controller";
import { WorkflowChatPanelComponent } from "../chat/workflow.chat.panel.component";
import { DraggableDirective } from "../../s25-movable/s25.movable.element.directive";
import { ApprovalGroupsStore } from "../../approval-groups/approval.groups.store";
import { WorkflowProgressDiagramStore } from "../workflow.progress.diagram.store";
import { S25RouterLinkComponent } from "../../s25-nav-bar/s25.router.link.component";
import { S25CollapseChevronComponent } from "../../s25-collapse-chevron/s25.collapse.chevron.component";
import { Budget } from "../../student-engagement/budget/Budget";
import { BudgetService } from "../../student-engagement/budget/budget.service";
import { S25PricingUtil } from "../../s25-pricing/s25.pricing.util";
import WorkflowSubmissionEntry = Workflow.WorkflowSubmissionEntry;
import AssignedWorkflowFacetOption = Workflow.AssignedWorkflowFacetOption;
import AssignedWorkflowFilters = Workflow.AssignedWorkflowFilters;
import MyTodoState = Workflow.MyTodoState;
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { S25LoadMoreComponent } from "../../../standalone/s25.load.more.component";
import { PaginatedLoadAnnouncer } from "../../student-engagement/search/paginated.load.announcer";
import { PaginatedLoadResult } from "../../student-engagement/search/paginated.list";
import { AriaLive } from "../../../services/aria.live.service";
import { TelemetryService } from "../../../services/telemetry.service";
import { ResponseMessage } from "../../../dataaccess/data.access";
import { S25Icons } from "../../s25-icon/s25.icon.const";
import { TypedTemplateContextDirective } from "../../../standalone/typed.template.directive";
import { S25Assert } from "../../../util/s25.assert";
import { S25Error } from "../../../util/core/s25.error";

const PAGE_SIZE = 25;
const KEYWORD_DEBOUNCE_MS = 300;
const WORKFLOW_ITEM_NOUN = { singular: "workflow item", plural: "workflow items" };
const TELEMETRY_CATEGORY = "MyWorkflow";

type PageRequest = { page: number; size: number; reason: "initial" | "filter" | "more" | "reload" };
type LoadedPage = { request: PageRequest; response: Workflow.AssignedWorkflowPage };
type FilterType = "keyword" | "status" | "org" | "form" | "submitter" | "date" | "actionable";
type TodoAction = "approve" | "deny" | "requestChanges" | "dismiss";
type CardOutcome = { text: string; icon: S25Icons.Name };

const TODO_ACTION_RESULTS: Record<
    TodoAction,
    { myTodoState: MyTodoState; announcement: string; telemetry: string; buttonClass: string }
> = {
    approve: { myTodoState: "APPROVED", announcement: "Approved", telemetry: "Approve", buttonClass: "approve-button" },
    deny: { myTodoState: "DENIED", announcement: "Denied", telemetry: "Deny", buttonClass: "deny-button" },
    requestChanges: {
        myTodoState: "REQUESTED_CHANGES",
        announcement: "Changes requested",
        telemetry: "RequestChanges",
        buttonClass: "request-changes-button",
    },
    dismiss: {
        myTodoState: "DISMISSED",
        announcement: "Dismissed",
        telemetry: "Dismiss",
        buttonClass: "dismiss-button",
    },
};
const RESET_BUTTON_CLASS = "reset-workflow-button";
const VIEW_HISTORY_BUTTON_CLASS = "view-history-button";

/** Keeps the selected option listed even when the other active filters leave it with no matches. */
function withSelectedOption(
    options: AssignedWorkflowFacetOption[] | undefined,
    selected: AssignedWorkflowFacetOption | undefined,
): AssignedWorkflowFacetOption[] {
    const facetOptions = options ?? [];
    if (!selected || facetOptions.some((option) => option.value === selected.value)) return facetOptions;
    return [...facetOptions, selected];
}

@TypeManagerDecorator("my-workflow-page")
@Component({
    selector: "my-workflow-page",
    template: `
        <div class="my-workflow-page">
            <div class="page-header">
                <div class="page-title-row">
                    <h1>My Workflow Items</h1>
                    @if (showActionableBadge()) {
                        <button type="button" class="count-badge-button" (click)="applyActionableFilter('HeaderBadge')">
                            <span class="count-badge count-badge--actionable"
                                >{{ actionableCount() }} need your action<span class="visually-hidden"
                                    >, show only these</span
                                ></span
                            >
                        </button>
                    }
                    @if (pendingCount()) {
                        <span class="count-badge pending">{{ pendingCount() }} Pending</span>
                    }
                    @if (changesRequestedCount()) {
                        <span class="count-badge changes-requested"
                            >{{ changesRequestedCount() }} Changes Requested</span
                        >
                    }
                </div>
                <button class="aw-button aw-button--outline" type="button" (click)="reloadLoadedItems()">
                    Refresh
                </button>
            </div>

            <div class="filters-container">
                <div class="search-wrapper">
                    <s25-ng-icon [type]="'search'" [size]="18" class="search-icon" [ariaLabel]="false" />
                    <input
                        #keywordInput
                        type="text"
                        class="search-input"
                        placeholder="Keyword search"
                        aria-label="Keyword search"
                        [disabled]="!hasLoadedOnce()"
                        [(ngModel)]="filterKeyword"
                    />
                    @if (filterKeyword()) {
                        <ng-container
                            *ngTemplateOutlet="clearButton; context: { type: 'keyword', name: 'keyword' }"
                        ></ng-container>
                    }
                </div>

                <div class="filter-controls">
                    <s25-ng-checkbox
                        #actionableCheckbox
                        class="actionable-filter"
                        [value]="filterActionable()"
                        [disabled]="!hasLoadedOnce()"
                        (valueChange)="onActionableCheckboxChange($event)"
                        >Needs my action</s25-ng-checkbox
                    >

                    <select
                        #statusSelect
                        class="filter-select"
                        aria-label="Filter by status"
                        [disabled]="!hasLoadedOnce()"
                        [(ngModel)]="filterStatus"
                    >
                        @for (opt of statusOptions; track opt.value) {
                            <option [value]="opt.value">{{ opt.label }}</option>
                        }
                    </select>

                    @if (showOrgSelect()) {
                        <select
                            #orgSelect
                            class="filter-select"
                            aria-label="Filter by organization"
                            [disabled]="!hasLoadedOnce()"
                            [ngModel]="filterOrg()?.value ?? ''"
                            (ngModelChange)="selectFacetOption(filterOrg, orgOptions(), $event)"
                        >
                            <option value="">All Organizations</option>
                            @for (org of orgOptions(); track org.value) {
                                <option [value]="org.value">{{ org.label }}</option>
                            }
                        </select>
                    }

                    @if (showFormSelect()) {
                        <select
                            #formSelect
                            class="filter-select"
                            aria-label="Filter by form"
                            [disabled]="!hasLoadedOnce()"
                            [ngModel]="filterForm()?.value ?? ''"
                            (ngModelChange)="selectFacetOption(filterForm, formOptions(), $event)"
                        >
                            <option value="">All Forms</option>
                            @for (form of formOptions(); track form.value) {
                                <option [value]="form.value">{{ form.label }}</option>
                            }
                        </select>
                    }

                    @if (showSubmitterSelect()) {
                        <select
                            #submitterSelect
                            class="filter-select"
                            aria-label="Filter by submitter"
                            [disabled]="!hasLoadedOnce()"
                            [ngModel]="filterSubmitter()?.value ?? ''"
                            (ngModelChange)="selectFacetOption(filterSubmitter, submitterOptions(), $event)"
                        >
                            <option value="">All Submitters</option>
                            @for (submitter of submitterOptions(); track submitter.value) {
                                <option [value]="submitter.value">{{ submitter.label }}</option>
                            }
                        </select>
                    }

                    <s25-datepicker
                        #dateFromPicker
                        [(value)]="filterDateFrom"
                        [allowEmpty]="true"
                        [autoSelectToday]="false"
                        [placeholder]="'Start date'"
                        [disabled]="!hasLoadedOnce()"
                    ></s25-datepicker>

                    <s25-datepicker
                        [(value)]="filterDateTo"
                        [allowEmpty]="true"
                        [autoSelectToday]="false"
                        [placeholder]="'End date'"
                        [disabled]="!hasLoadedOnce()"
                    ></s25-datepicker>

                    @if (hasActiveFilters()) {
                        <button class="text-btn" (click)="clearFilters()" type="button">Clear filters</button>
                    }
                </div>
            </div>

            @if (hasActiveFilters()) {
                <div class="active-filters">
                    <span class="filter-label">Active filters:</span>
                    @if (filterActionable()) {
                        <span class="filter-pill">
                            Needs my action
                            <ng-container
                                *ngTemplateOutlet="
                                    clearButton;
                                    context: { type: 'actionable', name: 'Needs my action' }
                                "
                            ></ng-container>
                        </span>
                    }
                    @if (filterKeyword()) {
                        <span class="filter-pill">
                            "{{ filterKeyword() }}"
                            <ng-container
                                *ngTemplateOutlet="clearButton; context: { type: 'keyword', name: 'keyword' }"
                            ></ng-container>
                        </span>
                    }
                    @if (filterStatus()) {
                        <span class="filter-pill">
                            {{ getStatusLabel() }}
                            <ng-container
                                *ngTemplateOutlet="clearButton; context: { type: 'status', name: getStatusLabel() }"
                            ></ng-container>
                        </span>
                    }
                    @if (filterOrg(); as org) {
                        <span class="filter-pill">
                            Org: {{ org.label }}
                            <ng-container
                                *ngTemplateOutlet="clearButton; context: { type: 'org', name: 'Org ' + org.label }"
                            ></ng-container>
                        </span>
                    }
                    @if (filterForm(); as form) {
                        <span class="filter-pill">
                            Form: {{ form.label }}
                            <ng-container
                                *ngTemplateOutlet="clearButton; context: { type: 'form', name: 'Form ' + form.label }"
                            ></ng-container>
                        </span>
                    }
                    @if (filterSubmitter(); as submitter) {
                        <span class="filter-pill">
                            Submitter: {{ submitter.label }}
                            <ng-container
                                *ngTemplateOutlet="
                                    clearButton;
                                    context: { type: 'submitter', name: 'Submitter ' + submitter.label }
                                "
                            ></ng-container>
                        </span>
                    }
                    @if (filterDateFrom() || filterDateTo()) {
                        <span class="filter-pill">
                            {{ getDateRangeLabel() }}
                            <ng-container
                                *ngTemplateOutlet="clearButton; context: { type: 'date', name: getDateRangeLabel() }"
                            ></ng-container>
                        </span>
                    }
                    @if (hasLoadedOnce()) {
                        <span class="results-count"
                            >{{ totalCount() }} {{ totalCount() === 1 ? "item" : "items" }}</span
                        >
                    }
                </div>
            }

            <div class="workflow-content">
                @if (!hasLoadedOnce() && !listLoadError()) {
                    <div class="loading">Loading your workflow items...</div>
                } @else if (listLoadError()) {
                    <div class="no-items" role="alert">
                        <h3>Could not load your workflow items</h3>
                        <button class="aw-button aw-button--outline" type="button" (click)="retryLoad()">Retry</button>
                    </div>
                } @else if (!submissions().length && !isRefiltering()) {
                    @if (!hasActiveFilters()) {
                        <div class="no-items">
                            <h3>No workflow items assigned to you</h3>
                            <p>When form submissions require your approval, they will appear here.</p>
                        </div>
                    } @else if (onlyActionableFilterActive()) {
                        <div class="no-items">
                            <h3>Nothing needs your action right now</h3>
                            <button class="text-btn" (click)="clearFilter('actionable')" type="button">
                                Show all items
                            </button>
                        </div>
                    } @else {
                        <div class="no-items">
                            <h3>No items match your filters</h3>
                            <p>Try adjusting your search criteria.</p>
                            <button class="text-btn" (click)="clearFilters()" type="button">Clear filters</button>
                        </div>
                    }
                } @else {
                    <button class="sort-toggle" type="button" (click)="sortAscending.set(!sortAscending())">
                        Sort by Submission Date
                        <s25-ng-collapse-chevron [expanded]="sortAscending()"></s25-ng-collapse-chevron>
                    </button>
                    <div
                        class="submissions-grid"
                        [class.submissions-grid--busy]="isRefiltering()"
                        [attr.aria-busy]="isRefiltering()"
                    >
                        @for (submission of submissions(); track submission.todoId) {
                            @let orgName = getSubmissionOrg(submission);
                            @let actionable = isActionable(submission);
                            <div
                                class="submission-card"
                                [class.submission-card--actionable]="actionable"
                                [id]="'workflow-item-' + submission.itemId"
                                [attr.data-todo-id]="submission.todoId"
                            >
                                <div class="card-header">
                                    <div class="submission-info">
                                        <div class="submission-title-row">
                                            <h3>
                                                {{ submission.workflowProcessName || "Workflow" }}
                                            </h3>
                                            @if (actionable) {
                                                <span class="needs-action-label">Needs your action</span>
                                            }
                                        </div>
                                        <span class="form-name">{{ submissionLabel(submission) }}</span>
                                        <div class="submission-meta">
                                            <span class="submission-id">ID: {{ submission.itemId }}</span>
                                            @if (submission.workflowStatus; as status) {
                                                <span
                                                    class="workflow-status-badge"
                                                    [class]="'status-' + status.toLowerCase().replace('_', '-')"
                                                >
                                                    {{ statusLabels[status] }}
                                                </span>
                                            }
                                            @if (getStageWaitingNote(submission); as waitingNote) {
                                                <span class="workflow-status-badge stage-waiting-badge">{{
                                                    waitingNote
                                                }}</span>
                                            }
                                        </div>
                                        @if (getSubmissionOrganizationId(submission); as organizationId) {
                                            <div class="submission-org">
                                                <s25-ng-icon
                                                    [type]="'organization'"
                                                    [size]="14"
                                                    [ariaLabel]="'Organization'"
                                                />
                                                <s25-ng-router-link
                                                    [text]="orgName ?? 'Organization #' + organizationId"
                                                    [stateName]="seOrganizationDetailsStateName"
                                                    [stateParams]="{ itemId: organizationId }"
                                                ></s25-ng-router-link>
                                            </div>
                                        }
                                    </div>
                                    <div class="card-actions">
                                        <button
                                            class="aw-button aw-button--outline"
                                            (click)="viewSubmissionDetails(submission)"
                                            [class.active]="selectedSubmission()?.itemId === submission.itemId"
                                        >
                                            {{ selectedSubmission()?.itemId === submission.itemId ? "Hide" : "View" }}
                                            Details
                                        </button>
                                    </div>
                                </div>

                                <div class="card-content">
                                    <div class="submission-summary">
                                        <div class="info-row">
                                            <label>Submitted by:</label>
                                            <span>
                                                <s25-ng-icon [type]="'user'" [size]="14" [ariaLabel]="false" />
                                                {{ submission.submitterName || "Unknown" }}
                                            </span>
                                        </div>
                                        <div class="info-row">
                                            <label>Submitted on:</label>
                                            <span>{{ formatDate(submission.submissionDate) }}</span>
                                        </div>
                                        <div class="info-row">
                                            <label>Current Stage:</label>
                                            <span>{{ submission.currentWorkflowStage?.stageName || "Unknown" }}</span>
                                        </div>
                                        @if (actionable) {
                                            <div class="info-row">
                                                <label>Current Approval:</label>
                                                <span>{{ submission.todoName || "Unknown" }}</span>
                                            </div>
                                        }
                                        @if (submission.approverNames?.length) {
                                            <div class="info-row">
                                                <label>Approvers:&nbsp;</label>
                                                <span>{{ formatApproverNamesForCard(submission.approverNames) }}</span>
                                            </div>
                                        }
                                        @if (
                                            getFormPreviewFields(
                                                submission,
                                                expandedPreviews().has(submission.todoId) ? 10 : 3
                                            );
                                            as preview
                                        ) {
                                            @if (preview.length) {
                                                <div class="form-preview">
                                                    @for (entry of preview; track entry.itemId) {
                                                        <div class="preview-field">
                                                            <span class="preview-label">{{ entry.itemName }}:</span>
                                                            <span class="preview-value">{{ entry.itemValue }}</span>
                                                        </div>
                                                    }
                                                    @if (
                                                        expandedPreviews().has(submission.todoId) ||
                                                        hasMorePreviewFields(submission, 3)
                                                    ) {
                                                        <button
                                                            class="preview-toggle"
                                                            (click)="
                                                                togglePreviewExpand(submission);
                                                                $event.stopPropagation()
                                                            "
                                                        >
                                                            {{
                                                                expandedPreviews().has(submission.todoId)
                                                                    ? "Show less"
                                                                    : "Show more..."
                                                            }}
                                                        </button>
                                                    }
                                                </div>
                                            }
                                        }
                                    </div>

                                    <!-- Workflow Actions -->
                                    <div class="workflow-actions">
                                        @let outcome = actionable ? null : getCardOutcome(submission);
                                        @if (actionable || outcome) {
                                            <div class="workflow-action-group workflow-action-group--decision">
                                                @if (actionable) {
                                                    <s25-ng-button
                                                        class="approve-button"
                                                        [type]="'success'"
                                                        [onClick]="approveSubmission"
                                                        [onClickArgs]="[submission]"
                                                    >
                                                        Approve
                                                    </s25-ng-button>
                                                    <s25-ng-button
                                                        class="deny-button"
                                                        [type]="'danger'"
                                                        [onClick]="denySubmission"
                                                        [onClickArgs]="[submission]"
                                                    >
                                                        Deny
                                                    </s25-ng-button>
                                                    <s25-ng-button
                                                        class="request-changes-button"
                                                        [type]="'warning'"
                                                        [onClick]="requestChanges"
                                                        [onClickArgs]="[submission]"
                                                    >
                                                        Request Changes
                                                    </s25-ng-button>
                                                    <s25-ng-button
                                                        class="dismiss-button"
                                                        [type]="'outline'"
                                                        [onClick]="dismissSubmission"
                                                        [onClickArgs]="[submission]"
                                                    >
                                                        Dismiss
                                                    </s25-ng-button>
                                                } @else if (outcome) {
                                                    <span class="workflow-outcome">
                                                        <s25-ng-icon
                                                            [type]="outcome.icon"
                                                            [size]="16"
                                                            [ariaLabel]="false"
                                                        />
                                                        {{ outcome.text }}
                                                    </span>
                                                }
                                            </div>
                                        }
                                        <div class="workflow-action-group workflow-action-group--tools">
                                            <s25-ng-button
                                                class="view-history-button"
                                                [type]="'outline'"
                                                [onClick]="viewWorkflowHistory"
                                                [onClickArgs]="[submission]"
                                            >
                                                View History
                                            </s25-ng-button>

                                            @if (canReset()) {
                                                <s25-ng-button
                                                    class="reset-workflow-button"
                                                    [type]="'danger--outline'"
                                                    [onClick]="resetWorkflow"
                                                    [onClickArgs]="[submission]"
                                                >
                                                    Reset Workflow
                                                </s25-ng-button>
                                            }

                                            <my-workflow-chat-dropdown
                                                [submission]="submission"
                                            ></my-workflow-chat-dropdown>

                                            <button
                                                class="aw-button aw-button--outline"
                                                (click)="toggleDiagram(submission)"
                                            >
                                                {{
                                                    showDiagram() &&
                                                    diagramStore.processInstanceId() ===
                                                        submission.workflowProcessInstanceId
                                                        ? "Hide"
                                                        : "Show"
                                                }}
                                                Diagram
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Expanded Details -->
                                @if (selectedSubmission()?.itemId === submission.itemId) {
                                    <div class="expanded-details">
                                        <div class="details-tabs">
                                            <button
                                                class="tab-button"
                                                [class.active]="activeTab() === 'form-data'"
                                                (click)="setActiveTab('form-data')"
                                            >
                                                Form Data
                                            </button>
                                            <button
                                                class="tab-button"
                                                [class.active]="activeTab() === 'history'"
                                                (click)="setActiveTab('history')"
                                            >
                                                History
                                            </button>
                                        </div>

                                        <div class="tab-content">
                                            @if (activeTab() === "form-data") {
                                                <div class="form-data-viewer">
                                                    @if (selectedSubmission()?.parent) {
                                                        <s25-ng-linked-forms
                                                            [items]="[selectedSubmission()!.parent!]"
                                                            [title]="'Parent Forms'"
                                                            [mode]="'parents'"
                                                        ></s25-ng-linked-forms>
                                                    }
                                                    @if (getIngestionSubmissionId(submission)) {
                                                        <s25-ng-ingestion-form
                                                            [submissionIdUuid]="getIngestionSubmissionId(submission)"
                                                            [readOnly]="true"
                                                        ></s25-ng-ingestion-form>
                                                    } @else if (
                                                        workflowSubmissionEntriesForDisplay(submission).length
                                                    ) {
                                                        <div class="form-fields">
                                                            @for (
                                                                entry of workflowSubmissionEntriesForDisplay(
                                                                    submission
                                                                );
                                                                track entry.itemName + ":" + (entry.itemId ?? "")
                                                            ) {
                                                                <div class="field-row">
                                                                    <label>{{ entry.itemName || "Field" }}:</label>
                                                                    <div class="field-value">
                                                                        {{
                                                                            formatWorkflowSubmissionEntryDisplayValue(
                                                                                entry
                                                                            )
                                                                        }}
                                                                    </div>
                                                                </div>
                                                            }
                                                        </div>
                                                    } @else {
                                                        <div class="no-data">No form data available</div>
                                                    }
                                                </div>
                                            }

                                            @if (activeTab() === "history") {
                                                <div class="history-viewer">
                                                    @if (loadingHistory()) {
                                                        <div class="loading">Loading history...</div>
                                                    } @else if (workflowHistory().length === 0) {
                                                        <div class="no-history">No workflow history available</div>
                                                    } @else {
                                                        <div class="history-timeline">
                                                            @for (todo of workflowHistory(); track todo.todoId) {
                                                                <div class="history-item">
                                                                    <div class="history-icon">
                                                                        @if (todo.curTodoState === 2) {
                                                                            <div class="icon approved">✓</div>
                                                                        } @else if (todo.curTodoState === 3) {
                                                                            <div class="icon denied">✗</div>
                                                                        } @else if (todo.curTodoState === 4) {
                                                                            <div class="icon requested-changes">↩</div>
                                                                        } @else if (todo.curTodoState === 99) {
                                                                            <div class="icon cancelled">✗</div>
                                                                        } @else if (todo.curTodoState === 1) {
                                                                            <div class="icon in-progress">⏳</div>
                                                                        }
                                                                    </div>
                                                                    <div class="history-content">
                                                                        <div class="history-title">
                                                                            @if (todo.curAssignedTo === AI_CONTACT_ID) {
                                                                                <s25-ng-icon
                                                                                    [type]="'aiSparkles'"
                                                                                    [ariaLabel]="false"
                                                                                    class="ai-sparkles-adjacent"
                                                                                />
                                                                            }
                                                                            {{ todo.todoName }}
                                                                        </div>
                                                                        <div class="history-meta">
                                                                            {{
                                                                                formatDate(
                                                                                    todo.compDt || todo.lastModDt
                                                                                )
                                                                            }}
                                                                            by
                                                                            {{
                                                                                todo.curAssignedTo === AI_CONTACT_ID
                                                                                    ? "AI Reviewer"
                                                                                    : todo.lastModUser
                                                                            }}
                                                                        </div>
                                                                        @if (todo.comment?.commentText) {
                                                                            <div class="history-comment">
                                                                                {{ todo.comment!.commentText }}
                                                                            </div>
                                                                        }
                                                                    </div>
                                                                </div>
                                                            }
                                                        </div>
                                                    }
                                                </div>
                                            }
                                        </div>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                    @if (loadMoreError()) {
                        <div class="load-more-error" role="alert">
                            Could not load more workflow items.
                            <button class="text-btn" type="button" (click)="retryLoad()">Retry</button>
                        </div>
                    }
                    <s25-ng-load-more
                        [currentCount]="submissions().length"
                        [totalCount]="totalCount()"
                        [isLoading]="isLoadingMore()"
                        [enableLoadAll]="false"
                        (loadMore)="loadMore()"
                    />
                }
            </div>

            @if (showDiagram()) {
                <div draggableElement [fitToViewport]="true" class="workflow-diagram-panel">
                    <workflow-diagram-viewer
                        [workflowProcess]="diagramProcess()"
                        [showProgress]="true"
                        [chatAvailable]="!!diagramStore.submission()"
                        [showDiagram]="showDiagram()"
                        (showDiagramChange)="diagramStore.hide()"
                        (chatWith)="openDiagramChat($event)"
                    ></workflow-diagram-viewer>
                </div>
            }
            <workflow-chat-panel [controller]="diagramChat" />

            <!-- Request Changes Modal -->
            @if (showRequestChangesModal()) {
                <div class="modal-overlay" (click)="hideRequestChangesModal()">
                    <div class="modal-content" (click)="$event.stopPropagation()">
                        <div class="modal-header">
                            <h3>Request Changes</h3>
                            <button class="modal-close" (click)="hideRequestChangesModal()">×</button>
                        </div>
                        <div class="modal-body">
                            <label for="changesText">Please describe the changes needed:</label>
                            <textarea
                                id="changesText"
                                [(ngModel)]="requestedChangesText"
                                class="form-control"
                                rows="4"
                                placeholder="Explain what needs to be changed..."
                            ></textarea>
                        </div>
                        <div class="modal-footer">
                            <button
                                class="aw-button aw-button--primary"
                                (click)="submitRequestChanges()"
                                [disabled]="submittingRequestChanges() || !requestedChangesText.trim()"
                            >
                                Request Changes
                            </button>
                            <button class="aw-button aw-button--outline" (click)="hideRequestChangesModal()">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            }
        </div>

        <ng-template #clearButton [templateContext]="clearButtonContext" let-type="type" let-name="name">
            <button
                type="button"
                class="clear-filter-button"
                [attr.aria-label]="'Remove filter: ' + name"
                (click)="clearFilter(type)"
            >
                ×
            </button>
        </ng-template>
    `,
    styles: `
        .my-workflow-page {
            container: my-workflow-page / inline-size;
            padding: 20px;
            max-width: 1200px;
            margin: 0 auto;
        }

        .page-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 2px solid var(--color--border);
        }

        .page-title-row {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 12px;
        }

        .page-header h1 {
            margin: 0;
            color: var(--color--text-strong);
        }

        .count-badge {
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 0.8125rem;
            font-weight: 600;
            white-space: nowrap;
        }

        .count-badge.pending {
            background: var(--cn-status-pending-bg);
            color: var(--cn-status-pending-fg);
        }

        .count-badge.changes-requested {
            background: var(--cn-status-changes-requested-bg);
            color: var(--cn-status-changes-requested-fg);
        }

        .count-badge-button {
            display: inline-flex;
            align-items: center;
            min-height: 44px;
            min-width: 44px;
            padding: 0;
            border: none;
            background: none;
            cursor: pointer;
        }

        .count-badge--actionable {
            background: var(--cn-status-actionable-bg);
            color: var(--cn-status-actionable-fg);
        }

        .count-badge-button:hover .count-badge--actionable {
            text-decoration: underline;
        }

        .filters-container {
            display: flex;
            gap: 1rem;
            margin-bottom: 1.5rem;
            flex-wrap: wrap;
        }

        .search-wrapper {
            flex: 1;
            min-width: 240px;
            position: relative;
        }

        .search-icon {
            position: absolute;
            left: 0.75rem;
            top: 50%;
            transform: translateY(-50%);
            color: var(--color--text-muted);
            pointer-events: none;
        }

        .search-input {
            width: 100%;
            padding: 0.5rem 2rem 0.5rem 2.5rem;
            border: 1px solid var(--color--border);
            border-radius: 6px;
            font-size: 0.875rem;
            transition: border-color 0.2s ease;
            background: var(--color--surface-bg);
            color: var(--color--text);
        }

        .search-input:focus {
            outline: none;
            border-color: var(--color--border-bold);
        }

        .search-wrapper button {
            position: absolute;
            right: 0.5rem;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            color: var(--color--text-muted);
            cursor: pointer;
            padding: 0.25rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 3px;
            font-size: 1.125rem;
            line-height: 1;
            transition: all 0.2s ease;
        }

        .filter-controls {
            display: flex;
            flex: 1 1 auto;
            flex-wrap: wrap;
            min-width: 0;
            gap: 0.75rem;
            align-items: center;
        }

        .actionable-filter {
            flex-shrink: 0;
            white-space: nowrap;
        }

        .sort-toggle {
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
            padding: 0;
            border: none;
            background: none;
            font-size: 0.9rem;
            cursor: pointer;
            color: var(--color--text-muted);
            margin-bottom: 0.75rem;
        }

        .sort-toggle:hover {
            color: var(--color--text-strong);
        }

        .filter-select {
            padding: 0.5rem 2rem 0.5rem 0.75rem;
            border: 1px solid var(--color--border);
            border-radius: 6px;
            font-size: 0.875rem;
            cursor: pointer;
            transition: border-color 0.2s ease;
            background: var(--color--surface-bg)
                url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")
                no-repeat right 0.5rem center;
            background-size: 16px;
            appearance: none;
            min-width: 140px;
            max-width: 240px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--color--text);
        }

        .filter-select:focus {
            outline: none;
            border-color: var(--color--border-bold);
        }

        ::ng-deep .filter-controls s25-datepicker .c-input {
            width: 100%;
            padding: 0.5rem 0.75rem;
            border: 1px solid var(--color--border) !important;
            border-radius: 6px;
            font-size: 0.875rem;
            transition: border-color 0.2s ease;
            background: var(--color--surface-bg);
            height: unset;
        }

        .text-btn {
            background: none;
            border: none;
            color: var(--color--text-muted);
            font-size: 0.875rem;
            cursor: pointer;
            padding: 0.5rem 0.75rem;
            border-radius: 6px;
            transition: all 0.2s ease;
        }

        .text-btn:hover {
            color: var(--color--text-strong);
            background: var(--color--surface-2-bg);
        }

        .active-filters {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            align-items: center;
            margin-bottom: 1rem;
            font-size: 0.875rem;
        }

        .filter-label {
            color: var(--color--text-muted);
        }

        .filter-pill {
            display: inline-flex;
            align-items: center;
            max-width: 100%;
            overflow-wrap: anywhere;
            gap: 0.375rem;
            padding: 0.25rem 0.625rem;
            background: var(--color--surface-2-bg);
            border-radius: 4px;
            font-size: 0.8125rem;
            color: var(--color--text-strong);
        }

        .filter-pill button {
            background: none;
            border: none;
            color: var(--color--text-muted);
            cursor: pointer;
            padding: 0;
            min-width: 24px;
            min-height: 24px;
            font-size: 1.125rem;
            line-height: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: color 0.2s ease;
        }

        .filter-pill button:hover {
            color: var(--color--text-strong);
        }

        .results-count {
            margin-left: auto;
            color: var(--color--text-muted);
            font-size: 0.875rem;
        }

        .loading,
        .no-items {
            text-align: center;
            padding: 60px 20px;
            color: var(--color--text-muted);
        }

        .no-items h3 {
            color: var(--color--text-strong);
            margin-bottom: 12px;
        }

        .submissions-grid {
            display: grid;
            gap: 20px;
            transition: opacity var(--transition-short) ease;
        }

        .submissions-grid--busy {
            opacity: 0.5;
        }

        .submission-card--actionable {
            border-left: 4px solid var(--cn-navy);
        }

        .submission-title-row {
            display: flex;
            align-items: baseline;
            flex-wrap: wrap;
            gap: 8px;
        }

        .needs-action-label {
            font-size: 0.8125rem;
            font-weight: 600;
            color: var(--color--link-text);
        }

        .workflow-status-badge.stage-waiting-badge {
            background: transparent;
            border: 1px solid var(--color--border);
            color: var(--color--text);
            font-weight: normal;
        }

        .workflow-status-badge {
            font-weight: bold;
            padding: 0.25rem 0.5rem;
            border-radius: var(--border-radius);
            font-size: 0.8125rem;
        }

        .workflow-status-badge.status-pending {
            background: color-mix(in srgb, var(--color--warning) 6%, var(--color--surface-bg));
            color: var(--color--warning);
        }

        .workflow-status-badge.status-in-progress {
            background: color-mix(in srgb, var(--cn-navy) 18%, var(--color--surface-bg));
            color: var(--color--link-text-hover);
        }

        .workflow-status-badge.status-completed {
            background: color-mix(in srgb, var(--color--text-success) 8%, var(--color--surface-bg));
            color: var(--color--text-success);
        }

        .workflow-status-badge.status-denied {
            background: color-mix(in srgb, var(--color--danger) 6%, var(--color--surface-bg));
            color: var(--color--text-danger);
        }

        .workflow-status-badge.status-requested-changes {
            background: var(--cn-status-changes-requested-bg);
            color: var(--cn-status-changes-requested-fg);
        }

        .workflow-status-badge.status-canceled {
            background: var(--color--surface-2-bg);
            color: var(--color--text);
        }

        .workflow-outcome {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 0.875rem;
            color: var(--color--text);
        }

        .load-more-error {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            margin-top: 1rem;
            color: var(--color--text-danger);
        }

        .submission-card {
            border: 1px solid var(--color--border);
            border-radius: 12px;
            background: var(--color--surface-bg);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            transition: box-shadow 0.2s;
            min-width: 0;
            overflow-wrap: anywhere;
            container-type: inline-size;
        }

        .submission-card:hover {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .card-header {
            padding: 20px;
            border-bottom: 1px solid var(--color--border);
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            justify-content: space-between;
            align-items: flex-start;
        }

        .submission-info {
            flex: 1 1 200px;
            min-width: 0;
        }

        .submission-info h3 {
            margin: 0 0 4px 0;
            color: var(--color--text-strong);
        }

        .form-name {
            display: block;
            font-size: 0.875rem;
            color: var(--color--text-muted);
            margin-bottom: 8px;
        }

        .submission-meta {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            align-items: center;
        }

        .submission-id {
            color: var(--color--text-muted);
            font-size: 14px;
        }

        .submission-org {
            color: var(--color--text);
            font-size: 13px;
            margin-top: 6px;
            display: flex;
            align-items: center;
            gap: 4px;
        }

        .form-preview {
            margin-top: 8px;
            padding: 8px 12px;
            background: var(--color--surface-2-bg);
            border-radius: 6px;
            border: 1px solid var(--color--border);
        }

        .preview-field {
            display: flex;
            gap: 8px;
            font-size: 0.8125rem;
            padding: 2px 0;
            min-width: 0;
        }

        .preview-label {
            flex-shrink: 0;
            max-width: 50%;
            color: var(--color--text-muted);
            font-weight: 500;
        }

        .preview-toggle {
            background: none;
            border: none;
            color: var(--color--link-text);
            font-size: 0.8125rem;
            cursor: pointer;
            padding: 4px 0 0;
            font-weight: 500;
        }

        .preview-toggle:hover {
            text-decoration: underline;
        }

        .preview-value {
            min-width: 0;
            color: var(--color--text-strong);
        }

        .card-content {
            padding: 20px;
        }

        .submission-summary {
            margin-bottom: 20px;
        }

        .info-row {
            display: flex;
            margin-bottom: 8px;
            align-items: flex-start;
        }

        .info-row label {
            font-weight: 500;
            color: var(--color--text);
            min-width: 120px;
            margin-right: 12px;
        }

        .info-row span {
            min-width: 0;
            color: var(--color--text-strong);
        }

        .requested-changes {
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 4px;
            padding: 8px;
            color: #856404;
            font-style: italic;
        }

        .workflow-actions,
        .workflow-action-group {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
        }

        @container (max-width: 600px) {
            .card-header {
                flex-direction: column;
                align-items: stretch;
                gap: 12px;
            }

            .card-actions,
            .card-actions .aw-button {
                width: 100%;
            }

            .workflow-actions {
                flex-direction: column;
                gap: 12px;
            }

            .workflow-action-group {
                display: grid;
                grid-template-columns: repeat(2, minmax(0, 1fr));
                gap: 8px;
                white-space: nowrap;
            }

            .workflow-action-group--decision + .workflow-action-group--tools {
                border-top: 1px solid var(--color--border);
                padding-top: 12px;
            }

            .workflow-outcome {
                grid-column: 1 / -1;
            }

            my-workflow-chat-dropdown:empty {
                display: none;
            }
        }

        @container (max-width: 300px) {
            .workflow-action-group {
                grid-template-columns: minmax(0, 1fr);
            }
        }

        .workflow-diagram-panel {
            height: auto;
            display: flex;
            flex-direction: column;
            overflow: auto;
            background: var(--color--surface-2-bg);
            border-right: 1px solid var(--color--border);
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            width: 95vw;
            max-width: 100vw;
        }

        .expanded-details {
            border-top: 1px solid var(--color--border);
            background: var(--color--surface-2-bg);
        }

        .details-tabs {
            display: flex;
            border-bottom: 1px solid var(--color--border);
        }

        .tab-button {
            padding: 12px 20px;
            border: none;
            background: none;
            cursor: pointer;
            border-bottom: 2px solid transparent;
            color: var(--color--text-muted);
        }

        .tab-button.active {
            color: var(--color--link-text);
            border-bottom-color: var(--color--link-text);
        }

        .tab-content {
            padding: 20px;
            min-width: 0;
        }

        .form-fields {
            display: grid;
            gap: 12px;
        }

        .field-row {
            display: grid;
            grid-template-columns: minmax(80px, 200px) minmax(0, 1fr);
            gap: 12px;
            padding: 8px 0;
            border-bottom: 1px solid var(--color--border);
        }

        .field-row label {
            font-weight: 500;
            color: var(--color--text);
        }

        .field-value {
            color: var(--color--text-strong);
            word-break: break-word;
        }

        .history-timeline {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .history-item {
            display: flex;
            gap: 12px;
            align-items: flex-start;
        }

        .history-icon {
            flex-shrink: 0;
        }

        .icon {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: bold;
        }

        .icon.approved {
            background: #d4edda;
            color: #155724;
        }

        .icon.denied {
            background: #f8d7da;
            color: #721c24;
        }

        .icon.pending {
            background: #d1ecf1;
            color: #0c5460;
        }

        .icon.cancelled {
            background: #fff3cd;
            color: #856404;
        }

        .history-content {
            flex: 1;
            min-width: 0;
        }

        .history-title {
            font-weight: 500;
            color: var(--color--text-strong);
            margin-bottom: 4px;
            display: flex;
            align-items: center;
            gap: 0.35em;
        }

        .ai-sparkles-adjacent {
            flex-shrink: 0;
        }

        .history-meta {
            font-size: 12px;
            color: var(--color--text-muted);
            margin-bottom: 8px;
        }

        .history-comment {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 4px;
            padding: 8px;
            color: var(--color--text-strong);
            font-style: italic;
            white-space: pre-wrap;
        }

        .no-data,
        .no-history {
            text-align: center;
            padding: 40px;
            color: var(--color--text-muted);
            font-style: italic;
        }

        /* Modal Styles */
        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
        }

        .modal-content {
            background: var(--color--surface-bg);
            border-radius: 8px;
            min-width: 400px;
            max-width: 500px;
            max-height: 80vh;
            overflow-y: auto;
        }

        .modal-header {
            padding: 20px;
            border-bottom: 1px solid var(--color--border);
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .modal-header h3 {
            margin: 0;
            color: var(--color--text-strong);
        }

        .modal-close {
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: var(--color--text-muted);
            padding: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .modal-body {
            padding: 20px;
        }

        .modal-body label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: var(--color--text);
        }

        .form-control {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            font-size: 14px;
            box-sizing: border-box;
            background: var(--color--surface-bg);
            color: var(--color--text);
        }

        .modal-footer {
            padding: 20px;
            border-top: 1px solid var(--color--border);
            display: flex;
            justify-content: flex-end;
            gap: 8px;
        }

        @container my-workflow-page (max-width: 768px) {
            .page-header {
                flex-wrap: wrap;
                gap: 12px;
            }

            .filters-container {
                flex-direction: column;
            }

            .filter-controls {
                flex-direction: column;
                align-items: stretch;
                width: 100%;
            }

            .filter-select {
                width: 100%;
                max-width: none;
            }
        }
    `,
    standalone: true,
    imports: [
        FormsModule,
        S25DropdownPaginatedModule,
        S25DatepickerModule,
        DataIngestionModule,
        LinkedFormsComponent,
        S25ButtonComponent,
        MyWorkflowChatDropdownComponent,
        S25IconComponent,
        NgTemplateOutlet,
        WorkflowDiagramViewerComponent,
        WorkflowChatPanelComponent,
        DraggableDirective,
        S25CollapseChevronComponent,
        S25RouterLinkComponent,
        S25CheckboxModule,
        S25LoadMoreComponent,
        TypedTemplateContextDirective,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [ApprovalGroupsStore],
})
export class MyWorkflowPageComponent {
    readonly AI_CONTACT_ID = Contact.AI_CONTACT_ID;
    readonly seOrganizationDetailsStateName = S25States.names.seOrganizationDetails;
    workflowHistory = signal<Task.DBTodo[]>([]);

    protected readonly diagramStore = inject(WorkflowProgressDiagramStore);
    protected readonly showDiagram = this.diagramStore.showDiagram;
    protected readonly diagramProcess = this.diagramStore.diagramProcess;
    protected readonly diagramChat = new WorkflowChatController();

    loadingHistory = signal(false);

    selectedSubmission = signal<WorkflowItem | null>(null);
    activeTab = signal<"form-data" | "history">("form-data");

    showRequestChangesModal = signal(false);
    readonly submittingRequestChanges = signal(false);
    requestedChangesText = "";
    private readonly handledRouteSubmissionId = signal<string | undefined>(undefined);

    private currentSubmissionForRequestChanges: WorkflowItem | null = null;

    private seSecurityStore = inject(SeSecurityStore);
    private stateStore = inject(StateStore);
    private injector = inject(Injector);
    private hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

    private readonly canResetResource = s25Resource({ loader: () => this.canResetWorkflow() });
    readonly canReset = computed(() => !!this.canResetResource.value());

    protected readonly clearButtonContext: { type: FilterType; name: string };

    private readonly keywordInput = viewChild<ElementRef<HTMLInputElement>>("keywordInput");
    private readonly actionableCheckbox = viewChild("actionableCheckbox", { read: ElementRef });
    private readonly statusSelect = viewChild<ElementRef<HTMLSelectElement>>("statusSelect");
    private readonly orgSelect = viewChild<ElementRef<HTMLSelectElement>>("orgSelect");
    private readonly formSelect = viewChild<ElementRef<HTMLSelectElement>>("formSelect");
    private readonly submitterSelect = viewChild<ElementRef<HTMLSelectElement>>("submitterSelect");
    private readonly dateFromPicker = viewChild("dateFromPicker", { read: ElementRef });

    // Filters
    filterKeyword = signal("");
    private readonly debouncedKeyword = signal("");
    filterStatus = signal<WorkflowStatus | "">("");
    filterOrg = signal<AssignedWorkflowFacetOption | undefined>(undefined);
    filterForm = signal<AssignedWorkflowFacetOption | undefined>(undefined);
    filterSubmitter = signal<AssignedWorkflowFacetOption | undefined>(undefined);
    filterDateFrom = signal<Date | null>(null);
    filterDateTo = signal<Date | null>(null);
    filterActionable = signal(false);
    sortAscending = signal(false);

    readonly expandedPreviews = signal(new Set<number>());

    private readonly filters = computed<AssignedWorkflowFilters>(() => ({
        keyword: this.debouncedKeyword(),
        status: this.filterStatus() || undefined,
        organizationId: this.filterOrg()?.value,
        formName: this.filterForm()?.value,
        submitterContId: this.filterSubmitter()?.value,
        submittedFrom: this.filterDateFrom() ?? undefined,
        submittedTo: this.filterDateTo() ?? undefined,
        actionableOnly: this.filterActionable(),
        sortAscending: this.sortAscending(),
    }));

    /** Any filter or sort change restarts at page 0; Load more and reloads set this directly. */
    private readonly pageRequest = linkedSignal<AssignedWorkflowFilters, PageRequest>({
        source: this.filters,
        computation: (_filters, previous) => ({ page: 0, size: PAGE_SIZE, reason: previous ? "filter" : "initial" }),
    });

    private readonly assignedResource = s25Resource({
        params: () => ({ filters: this.filters(), request: this.pageRequest() }),
        loader: async ({ params }): Promise<LoadedPage> => {
            const response = await WorkflowService.getAssignedWorkflowPage(
                params.filters,
                params.request.page,
                params.request.size,
            );
            if (params.request.reason === "initial" || params.request.reason === "reload") {
                this.refreshAssignedCountBadge();
            }
            return { request: params.request, response };
        },
    });

    private readonly lastLoadedPage = computed(() => this.assignedResource.lastValue());
    private readonly facets = computed(() => this.lastLoadedPage()?.response.facets);

    /** Loaded cards: a new page 0 replaces them, Load more appends, and actions patch them in place. */
    readonly submissions = linkedSignal<LoadedPage | undefined, WorkflowItem[]>({
        source: () => this.assignedResource.value(),
        computation: (loaded, previous) => {
            const previousItems = previous?.value ?? [];
            if (!loaded) return previousItems;
            if (loaded.request.reason !== "more") return loaded.response.items;
            const loadedTodoIds = new Set(previousItems.map((item) => item.todoId));
            return [...previousItems, ...loaded.response.items.filter((item) => !loadedTodoIds.has(item.todoId))];
        },
    });

    /** An action patched a loaded card, so it may have left the server's filter and shifted page offsets. */
    private readonly loadedListPatched = linkedSignal(() => {
        this.assignedResource.value();
        return false;
    });

    readonly hasLoadedOnce = computed(() => !!this.lastLoadedPage());
    readonly totalCount = computed(() => this.lastLoadedPage()?.response.totalCount ?? 0);
    private readonly hasMore = computed(() => this.submissions().length < this.totalCount());
    private readonly isLoading = computed(() => this.assignedResource.isLoading());
    readonly isLoadingMore = computed(() => this.isLoading() && this.pageRequest().reason === "more");
    readonly isRefiltering = computed(
        () => this.isLoading() && this.hasLoadedOnce() && this.pageRequest().reason !== "more",
    );
    private readonly loadError = computed(() => !this.isLoading() && this.assignedResource.error());
    readonly listLoadError = computed(() => !!this.loadError() && this.pageRequest().reason !== "more");
    readonly loadMoreError = computed(() => !!this.loadError() && this.pageRequest().reason === "more");

    readonly pendingCount = computed(() => this.facets()?.statusCounts?.PENDING ?? 0);
    readonly changesRequestedCount = computed(() => this.facets()?.statusCounts?.REQUESTED_CHANGES ?? 0);
    /** Decremented locally after an action; the server value only changes on the next load. */
    readonly actionableCount = linkedSignal(() => this.facets()?.actionableCount ?? 0);
    /** Hides only after a load reports zero, so the badge never unmounts under focus after a local decrement. */
    readonly showActionableBadge = computed(() => (this.facets()?.actionableCount ?? 0) > 0);

    readonly orgOptions = computed(() => withSelectedOption(this.facets()?.organizations, this.filterOrg()));
    readonly formOptions = computed(() => withSelectedOption(this.facets()?.forms, this.filterForm()));
    readonly submitterOptions = computed(() => withSelectedOption(this.facets()?.submitters, this.filterSubmitter()));
    /** Some items have no organization, so even a single option can narrow the list. */
    readonly showOrgSelect = computed(() => this.orgOptions().length > 0 || !!this.filterOrg());
    readonly showFormSelect = computed(() => this.formOptions().length > 1 || !!this.filterForm());
    readonly showSubmitterSelect = computed(() => this.submitterOptions().length > 1 || !!this.filterSubmitter());

    readonly statusLabels: Record<WorkflowStatus, string> = {
        PENDING: "Pending",
        IN_PROGRESS: "In Progress",
        COMPLETED: "Completed",
        DENIED: "Denied",
        REQUESTED_CHANGES: "Requested Changes",
        CANCELED: "Canceled",
    };

    readonly statusOptions: { value: WorkflowStatus | ""; label: string }[] = [
        { value: "", label: "All Statuses" },
        ...Object.entries(this.statusLabels).map(([value, label]) => ({ value: value as WorkflowStatus, label })),
    ];

    readonly hasActiveFilters = computed(() => {
        return (
            !!this.filterKeyword() ||
            !!this.filterStatus() ||
            !!this.filterOrg() ||
            !!this.filterForm() ||
            !!this.filterSubmitter() ||
            !!this.filterDateFrom() ||
            !!this.filterDateTo() ||
            this.filterActionable()
        );
    });

    readonly onlyActionableFilterActive = computed(() => {
        const filters = this.filters();
        return (
            filters.actionableOnly &&
            !this.filterKeyword() &&
            !filters.status &&
            !filters.organizationId &&
            !filters.formName &&
            !filters.submitterContId &&
            !filters.submittedFrom &&
            !filters.submittedTo
        );
    });

    private readonly announcedList: PaginatedLoadAnnouncer.AnnouncedList = {
        items: this.submissions,
        totalCount: this.totalCount,
        hasMore: this.hasMore,
        loading: this.isLoadingMore,
        loadingAll: computed(() => false),
        loadMore: () => this.fetchNextPage(),
        loadAll: () => this.fetchNextPage(),
    };

    clearFilters() {
        this.keywordInput()?.nativeElement.focus();
        this.filterKeyword.set("");
        this.debouncedKeyword.set("");
        this.filterStatus.set("");
        this.filterOrg.set(undefined);
        this.filterForm.set(undefined);
        this.filterSubmitter.set(undefined);
        this.filterDateFrom.set(null);
        this.filterDateTo.set(null);
        this.filterActionable.set(false);
    }

    clearFilter(type: FilterType) {
        this.focusFilterControl(type);
        switch (type) {
            case "keyword":
                this.filterKeyword.set("");
                return this.debouncedKeyword.set("");
            case "status":
                return this.filterStatus.set("");
            case "org":
                return this.filterOrg.set(undefined);
            case "form":
                return this.filterForm.set(undefined);
            case "submitter":
                return this.filterSubmitter.set(undefined);
            case "actionable":
                return this.filterActionable.set(false);
            case "date":
                this.filterDateTo.set(null);
                this.filterDateFrom.set(null);
                return;
            default:
                S25Assert.never(type, "Unknown workflow filter");
        }
    }

    /** Moves focus to the control a filter pill represents, falling back to the keyword input if it unmounts. */
    private focusFilterControl(type: FilterType) {
        const control = this.filterControlElement(type);
        (control ?? this.keywordInput()?.nativeElement)?.focus();
        afterNextRender(
            () => {
                const focusWasDropped = !document.activeElement || document.activeElement === document.body;
                if (focusWasDropped) this.keywordInput()?.nativeElement.focus();
            },
            { injector: this.injector },
        );
    }

    private filterControlElement(type: FilterType): HTMLElement | undefined {
        switch (type) {
            case "keyword":
                return this.keywordInput()?.nativeElement;
            case "status":
                return this.statusSelect()?.nativeElement;
            case "org":
                return this.orgSelect()?.nativeElement;
            case "form":
                return this.formSelect()?.nativeElement;
            case "submitter":
                return this.submitterSelect()?.nativeElement;
            case "actionable":
                return this.actionableCheckbox()?.nativeElement.querySelector("input") ?? undefined;
            case "date":
                return this.dateFromPicker()?.nativeElement.querySelector("input, button") ?? undefined;
            default:
                S25Assert.never(type, "Unknown workflow filter");
        }
    }

    selectFacetOption(
        filter: WritableSignal<AssignedWorkflowFacetOption | undefined>,
        options: AssignedWorkflowFacetOption[],
        value: string,
    ) {
        filter.set(options.find((option) => option.value === value));
    }

    onActionableCheckboxChange(checked: boolean) {
        if (checked) return this.applyActionableFilter("Checkbox");
        this.filterActionable.set(false);
    }

    applyActionableFilter(source: "Checkbox" | "HeaderBadge") {
        if (this.filterActionable()) return;
        this.filterActionable.set(true);
        TelemetryService.sendWithSub(TELEMETRY_CATEGORY, "ActionableFilter", source);
    }

    loadMore() {
        TelemetryService.send(TELEMETRY_CATEGORY, "LoadMore");
        void PaginatedLoadAnnouncer.loadWithAnnouncements(this.announcedList, "more", WORKFLOW_ITEM_NOUN, PAGE_SIZE);
    }

    private async fetchNextPage(): Promise<PaginatedLoadResult> {
        const loadedCount = this.submissions().length;
        this.pageRequest.set(
            this.loadedListPatched()
                ? { page: 0, size: loadedCount + PAGE_SIZE, reason: "more" }
                : { page: Math.floor(loadedCount / PAGE_SIZE), size: PAGE_SIZE, reason: "more" },
        );
        try {
            await this.assignedResource.awaitLoad();
        } catch (error) {
            return { loaded: 0, error };
        }
        const error = this.assignedResource.error();
        if (error) return { loaded: 0, error };
        return { loaded: this.submissions().length - loadedCount };
    }

    /** One reload with the current filters that keeps every loaded card (page 0, size = loaded count). */
    reloadLoadedItems() {
        this.pageRequest.set({ page: 0, size: Math.max(this.submissions().length, PAGE_SIZE), reason: "reload" });
    }

    retryLoad() {
        this.assignedResource.resource.reload();
    }

    submissionLabel(submission: WorkflowItem): string {
        return submission.name || "Form Submission";
    }

    isActionable(submission: WorkflowItem): boolean {
        return submission.myTodoState === "IN_PROGRESS" && !submission.stateClosed;
    }

    getCardOutcome(submission: WorkflowItem): CardOutcome | undefined {
        switch (submission.myTodoState) {
            case "APPROVED":
                return { text: "You approved", icon: "check" };
            case "DENIED":
                return { text: "You denied", icon: "x" };
            case "REQUESTED_CHANGES":
                return { text: "You requested changes", icon: "undo" };
            case "DISMISSED":
                return { text: "You dismissed this", icon: "minus" };
            case "CANCELLED":
                return { text: "No longer needs your action", icon: "info" };
            case "IN_PROGRESS":
                return submission.stateClosed ? this.getClosedByOtherOutcome(submission) : undefined;
            case undefined:
                return undefined;
            default:
                S25Assert.never(submission.myTodoState, "Unknown workflow todo state");
        }
    }

    private getClosedByOtherOutcome(submission: WorkflowItem): CardOutcome {
        const closedBy = submission.stateClosedByName;
        if (closedBy) {
            switch (submission.stateClosedAction) {
                case "APPROVED":
                    return { text: `Approved by ${closedBy}`, icon: "check" };
                case "DENIED":
                    return { text: `Denied by ${closedBy}`, icon: "x" };
                case "REQUESTED_CHANGES":
                    return { text: `Changes requested by ${closedBy}`, icon: "undo" };
            }
        }
        return { text: "Handled by another approver", icon: "userCheck" };
    }

    getStageWaitingNote(submission: WorkflowItem): string | undefined {
        if (submission.currentWorkflowStage?.isAccumulator) return "Waiting on linked forms";
        if (submission.currentWorkflowStage?.isApAccumulator) return "Waiting on assignment policy";
    }

    getStatusLabel(): string {
        return this.statusOptions.find((opt) => opt.value === this.filterStatus())?.label ?? "All Statuses";
    }

    getDateRangeLabel(): string {
        const from = S25Util.date.toStartOfDay(this.filterDateFrom());
        const to = S25Util.date.toEndOfDay(this.filterDateTo());
        if (from && to) return `${this.formatDate(from)} – ${this.formatDate(to)}`;
        if (from) return `From ${this.formatDate(from)}`;
        if (to) return `Until ${this.formatDate(to)}`;
        return "Date range";
    }

    constructor() {
        // Auto-refresh when component becomes visible (transitions from hidden to visible)
        effect(() => {
            const isVisible = this.stateStore.currentSeState() === S25States.names.seMyWorkflow;
            if (isVisible && untracked(this.hasLoadedOnce)) {
                untracked(() => this.reloadLoadedItems());
            }
        });

        effect((onCleanup) => {
            const keyword = this.filterKeyword();
            const timerId = setTimeout(() => this.debouncedKeyword.set(keyword), KEYWORD_DEBOUNCE_MS);
            onCleanup(() => clearTimeout(timerId));
        });

        effect((onCleanup) => {
            if (!this.isRefiltering() || untracked(this.pageRequest).reason !== "filter") return;
            const timerId = setTimeout(
                () => void AriaLive.announce("Loading workflow items"),
                PaginatedLoadAnnouncer.PENDING_ANNOUNCEMENT_DELAY_MS,
            );
            onCleanup(() => clearTimeout(timerId));
        });

        effect(() => {
            const loaded = this.assignedResource.value();
            if (loaded?.request.reason !== "filter") return;
            const count = loaded.response.totalCount;
            void AriaLive.announce(`${count} ${count === 1 ? "item" : "items"}`);
        });

        effect(() => {
            if (!this.assignedResource.value()) return;
            untracked(() => this.openSubmissionFromRoute());
        });

        // Scroll to and reveal the selected submission card
        effect((onCleanup) => {
            const submission = this.selectedSubmission();
            if (!submission?.itemId) return;
            const timerId = setTimeout(() => {
                document
                    .getElementById(`workflow-item-${submission.itemId}`)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 0);
            onCleanup(() => clearTimeout(timerId));
        });
    }

    /**
     * Check if user can reset workflows (SE admin only)
     * Isolated method so permission logic can be swapped later
     */
    private async canResetWorkflow(): Promise<boolean> {
        await this.seSecurityStore.ensureInitialized();
        return this.seSecurityStore.hasFLSOverride();
    }

    private dateFormatResp = s25Resource({
        loader: () => UserprefService.getS25DateTimeformat(),
    });

    private dateFormat = computed(() => this.dateFormatResp.value() ?? "");

    private fiscalYearNameMap = computed(() => this.fiscalYearNamesResp.value());

    private fiscalYearNamesResp = s25Resource({
        loader: () => BudgetService.getFiscalYearNameMap(),
    });

    /** Opens a deep-linked submission once, paging forward until it is loaded or the list runs out. */
    private openSubmissionFromRoute() {
        const submissionId = StateService.getRouteHistory()?.at(-1)?.to?._params?.submissionId;
        if (!submissionId || submissionId === this.handledRouteSubmissionId()) return;
        const match = this.submissions().find((submission) => submission.submissionIdUuid === submissionId);
        if (match) {
            this.handledRouteSubmissionId.set(submissionId);
            if (this.selectedSubmission()?.itemId !== match.itemId) void this.viewSubmissionDetails(match);
            return;
        }
        if (this.hasMore()) {
            void this.fetchNextPage();
            return;
        }
        this.handledRouteSubmissionId.set(submissionId);
        void AlertService.alert("This workflow item could not be found in your workflow list.");
    }

    private refreshAssignedCountBadge() {
        WorkflowService.getAssignedWorkflowCount()
            .then((count) => this.seSecurityStore.setAssignedWorkflowPendingCount(count))
            .catch(() => {});
    }

    private async loadSubmissionWithLinkedForms(submission: WorkflowItem): Promise<void> {
        try {
            const submissionWithLinkedForms = await WorkflowService.getWorkflowItemWithLinkedForms(
                submission.itemTypeId!,
                submission.itemId!,
            );
            this.selectedSubmission.set(submissionWithLinkedForms);
        } catch (error) {
            console.error("Error loading linked forms:", error);
            // Fallback to submission without linked forms
            this.selectedSubmission.set(submission);
        }
    }

    async viewSubmissionDetails(submission: WorkflowItem) {
        const currentSubmission = this.selectedSubmission();
        if (
            // If the submission is already selected, close the details
            currentSubmission &&
            currentSubmission.itemId === submission.itemId &&
            currentSubmission.itemTypeId === submission.itemTypeId
        ) {
            this.selectedSubmission.set(null);
        } else {
            await this.loadSubmissionWithLinkedForms(submission);
            this.setActiveTab("form-data");
        }
    }

    async setActiveTab(tab: "form-data" | "history") {
        this.activeTab.set(tab);
        if (tab === "history" && this.selectedSubmission()) {
            this.loadWorkflowHistoryForSubmission(this.selectedSubmission()!);
        } else if (tab === "form-data" && this.selectedSubmission()) {
            // Reload submission with linked forms when switching to form-data tab
            // to ensure parent forms are loaded
            await this.loadSubmissionWithLinkedForms(this.selectedSubmission()!);
        }
    }

    viewWorkflowHistory = async (submission: WorkflowItem) => {
        this.selectedSubmission.set(submission);
        this.setActiveTab("history");
    };

    async loadWorkflowHistoryForSubmission(submission: WorkflowItem) {
        if (!submission.itemTypeId || !submission.itemId) return;
        this.loadingHistory.set(true);
        try {
            const history = await WorkflowService.getWorkflowHistory(submission.itemTypeId, submission.itemId);
            this.workflowHistory.set(history);
        } catch (error) {
            console.error("Error loading workflow history:", error);
            S25Util.showError(error);
        } finally {
            this.loadingHistory.set(false);
        }
    }

    approveSubmission = (submission: WorkflowItem) =>
        this.runTodoAction(submission, "approve", {
            confirmMessage: "Are you sure you want to approve this submission?",
            failureMessage: "Approval failed. The workflow action may have an incorrect configuration.",
            perform: (todoId) => WorkflowService.approveSubmissionFromTodo(todoId),
        });

    denySubmission = (submission: WorkflowItem) =>
        this.runTodoAction(submission, "deny", {
            confirmMessage: "Are you sure you want to deny this submission?",
            failureMessage: "Denial failed. The workflow action may have an incorrect configuration.",
            perform: (todoId) => WorkflowService.denySubmissionFromTodo(todoId),
        });

    dismissSubmission = (submission: WorkflowItem) =>
        this.runTodoAction(submission, "dismiss", {
            confirmMessage: "Remove this item from your queue? This does not approve or deny the submission.",
            failureMessage: "Failed to dismiss this item from your queue.",
            perform: (todoId) => WorkflowService.dismissSubmissionFromTodo(todoId),
        });

    private async runTodoAction(
        submission: WorkflowItem,
        action: Exclude<TodoAction, "requestChanges">,
        options: {
            confirmMessage: string;
            failureMessage: string;
            perform: (todoId: number) => Promise<ResponseMessage>;
        },
    ) {
        const triggerClass = TODO_ACTION_RESULTS[action].buttonClass;
        if (!(await AlertService.confirm(options.confirmMessage))) {
            return this.refocusCardButtonAfterItEnables(submission, triggerClass);
        }
        if (!submission.todoId) {
            S25Error.show("No todo ID found for this submission");
            return this.refocusCardButtonAfterItEnables(submission, triggerClass);
        }

        try {
            const result = await options.perform(submission.todoId);
            if (result?.messageCode === "conflict") return this.markHandledByAnotherApprover(submission);
            if (result?.messageCode !== "success") {
                S25Error.show(result?.message || options.failureMessage);
                return this.refocusCardButtonAfterItEnables(submission, triggerClass);
            }
            this.completeTodoAction(submission, action);
        } catch (error) {
            if (isConflictError(error)) return this.markHandledByAnotherApprover(submission);
            console.error(`Error running workflow action "${action}":`, error);
            S25Util.showError(error);
            this.refocusCardButtonAfterItEnables(submission, triggerClass);
        }
    }

    private completeTodoAction(submission: WorkflowItem, action: TodoAction) {
        const result = TODO_ACTION_RESULTS[action];
        this.patchSubmission(submission, { myTodoState: result.myTodoState });
        this.refreshAssignedCountBadge();
        TelemetryService.sendWithSub(TELEMETRY_CATEGORY, "Outcome", result.telemetry);
        void AriaLive.announce(`${result.announcement}: ${this.submissionLabel(submission)}`);
        this.focusViewHistory(submission);
    }

    private markHandledByAnotherApprover(submission: WorkflowItem) {
        this.patchSubmission(submission, { stateClosed: true });
        void AriaLive.announce(`Handled by another approver: ${this.submissionLabel(submission)}`);
        this.focusViewHistory(submission);
    }

    private patchSubmission(submission: WorkflowItem, patch: Partial<WorkflowItem>) {
        const currentItem = this.submissions().find((item) => item.todoId === submission.todoId);
        const wasActionable = !!currentItem && this.isActionable(currentItem);
        this.submissions.update((submissions) =>
            submissions.map((item) => (item.todoId === submission.todoId ? { ...item, ...patch } : item)),
        );
        this.loadedListPatched.set(true);
        if (wasActionable) this.actionableCount.update((count) => Math.max(count - 1, 0));
    }

    private focusViewHistory(submission: WorkflowItem) {
        afterNextRender(() => this.cardButton(submission, VIEW_HISTORY_BUTTON_CLASS)?.focus(), {
            injector: this.injector,
        });
    }

    /** `s25-ng-button` disables itself until its click promise settles, so focus has to wait a tick. */
    private refocusCardButtonAfterItEnables(submission: WorkflowItem, buttonClass: string) {
        setTimeout(() => this.cardButton(submission, buttonClass)?.focus());
    }

    private cardButton(submission: WorkflowItem, buttonClass: string): HTMLElement | null {
        return this.hostElement.nativeElement.querySelector<HTMLElement>(
            `[data-todo-id="${submission.todoId}"] .${buttonClass} button`,
        );
    }

    requestChanges = async (submission: WorkflowItem) => {
        this.currentSubmissionForRequestChanges = submission;
        this.requestedChangesText = "";
        this.showRequestChangesModal.set(true);
    };

    hideRequestChangesModal() {
        const submission = this.currentSubmissionForRequestChanges;
        this.closeRequestChangesModal();
        if (submission)
            this.refocusCardButtonAfterItEnables(submission, TODO_ACTION_RESULTS.requestChanges.buttonClass);
    }

    private closeRequestChangesModal() {
        this.showRequestChangesModal.set(false);
        this.currentSubmissionForRequestChanges = null;
        this.requestedChangesText = "";
    }

    async submitRequestChanges() {
        const submission = this.currentSubmissionForRequestChanges;
        if (this.submittingRequestChanges() || !submission?.todoId || !this.requestedChangesText.trim()) {
            return;
        }

        this.submittingRequestChanges.set(true);
        try {
            const result = await WorkflowService.requestChangesFromTodo(
                submission.todoId,
                this.requestedChangesText.trim(),
            );
            this.closeRequestChangesModal();
            if (result?.messageCode === "conflict") return this.markHandledByAnotherApprover(submission);
            this.completeTodoAction(submission, "requestChanges");
        } catch (error) {
            if (isConflictError(error)) {
                this.closeRequestChangesModal();
                return this.markHandledByAnotherApprover(submission);
            }
            console.error("Error requesting changes:", error);
            S25Util.showError(error);
        } finally {
            this.submittingRequestChanges.set(false);
        }
    }

    formatDate(date: ISODateString | Date | undefined): string | undefined {
        return S25Datefilter.transform(date, this.dateFormat());
    }

    //For non form workflow (eg. budgets) we want to display something other than the raw entry
    formatWorkflowSubmissionEntryDisplayValue(entry: WorkflowSubmissionEntry): string {
        const rawValue = entry.itemValue ?? "";
        if (!rawValue) return "";

        const fieldTypeId = Number(entry.itemTypeId);
        const budgetFieldTypes = Budget.WORKFLOW_SUBMISSION_FIELD_TYPES;

        switch (fieldTypeId) {
            case budgetFieldTypes.budgetPlanType:
                return Budget.budgetPlanTypeDisplayName(rawValue) ?? rawValue;
            case budgetFieldTypes.budgetPlanAllocatedAmount: {
                const amount = Number(rawValue);
                return Number.isFinite(amount) ? S25PricingUtil.formatCurrency(amount) : rawValue;
            }
            case budgetFieldTypes.budgetPlanStartDate:
            case budgetFieldTypes.budgetPlanEndDate:
                return this.formatDate(rawValue) ?? rawValue;
            case budgetFieldTypes.fiscalYear: {
                const fiscalYearId = Number(rawValue);
                if (Number.isFinite(fiscalYearId)) {
                    return this.fiscalYearNameMap()?.get(fiscalYearId) ?? rawValue;
                }
                return rawValue;
            }
            default:
                return rawValue;
        }
    }

    /** Limits long approver lists on submission cards for readability. */
    formatApproverNamesForCard(approverNames: string[] | undefined): string {
        if (!approverNames?.length) return "";
        const displayedApproverNameCount = 10;
        const visibleNames = approverNames.slice(0, displayedApproverNameCount);
        const joined = visibleNames.join(", ");
        if (approverNames.length <= displayedApproverNameCount) {
            return joined;
        }
        return `${joined}, ...`;
    }

    getIngestionSubmissionId(submission: WorkflowItem): string | null {
        // For workflow submissions, the itemId is the submissionId
        if (submission.itemTypeId === Workflow.WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE) {
            return submission.submissionIdUuid || null;
        }
        return null;
    }

    isBudgetPlanWorkflowItem(submission: WorkflowItem): boolean {
        return submission.itemTypeId === Workflow.BUDGET_PLAN_OBJECT_TYPE;
    }

    getSubmissionOrganizationId(submission: WorkflowItem): number | undefined {
        return submission.organizationId ?? undefined;
    }

    workflowSubmissionEntriesForDisplay(submission: WorkflowItem): Workflow.WorkflowSubmissionEntry[] {
        const list = submission.workflowSubmissionList;
        if (!list?.length) {
            return [];
        }
        if (!this.isBudgetPlanWorkflowItem(submission)) {
            return list;
        }
        return list.filter((entry) => Number(entry.itemId) !== INGESTION_SYSTEM_FIELDS.ORGANIZATION);
    }

    getFormPreviewFields(submission: WorkflowItem, limit = 3): Workflow.WorkflowSubmissionEntry[] {
        return this.previewEntries(submission).slice(0, limit);
    }

    hasMorePreviewFields(submission: WorkflowItem, limit: number): boolean {
        return this.previewEntries(submission).length > limit;
    }

    private previewEntries(submission: WorkflowItem): WorkflowSubmissionEntry[] {
        if (this.isBudgetPlanWorkflowItem(submission)) return [];
        if (!submission.workflowSubmissionList?.length) return [];
        const systemIds = new Set(Object.values(INGESTION_SYSTEM_FIELDS).map(Number));
        return submission.workflowSubmissionList.filter(
            (entry) =>
                entry.itemName &&
                entry.itemValue &&
                !systemIds.has(Number(entry.itemId)) &&
                isPrimitiveFieldType(entry.fieldType),
        );
    }

    togglePreviewExpand(submission: WorkflowItem) {
        if (submission.todoId == null) return;
        const next = new Set(this.expandedPreviews());
        if (next.has(submission.todoId)) {
            next.delete(submission.todoId);
        } else {
            next.add(submission.todoId);
        }
        this.expandedPreviews.set(next);
    }

    protected getSubmissionOrg(submission: WorkflowItem) {
        return submission?.organizationName;
    }

    toggleDiagram(submission: WorkflowItem) {
        this.diagramStore.showForInstance(
            submission.workflowProcessId,
            submission.workflowProcessInstanceId,
            submission,
        );
    }

    openDiagramChat(request: DiagramChatRequest) {
        const submission = this.diagramStore.submission();
        if (!submission) return;
        void this.diagramChat.start(
            submission,
            { kind: "person", contId: request.contId, name: request.name, chatUser: request.chatUser },
            request.returnFocusTo,
        );
    }

    resetWorkflow = async (submission: WorkflowItem) => {
        if (
            !(await AlertService.confirm(
                "Are you sure you want to reset this workflow? This will delete all workflow progress and restart from the beginning using the current workflow definition.",
            ))
        ) {
            return this.refocusCardButtonAfterItEnables(submission, RESET_BUTTON_CLASS);
        }

        try {
            if (!submission.itemTypeId || !submission.itemId) {
                S25Util.showError("Invalid workflow item");
                return this.refocusCardButtonAfterItEnables(submission, RESET_BUTTON_CLASS);
            }

            await WorkflowService.resetWorkflow(submission.itemTypeId, submission.itemId);
            this.reloadLoadedItems();
        } catch (error) {
            console.error("Error resetting workflow:", error);
            S25Util.showError(error);
            this.refocusCardButtonAfterItEnables(submission, RESET_BUTTON_CLASS);
        }
    };
}

function isConflictError(error: unknown): boolean {
    return (error as { status?: number })?.status === 409;
}
